import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DeviceMeasurement } from '../../../../model/device-measurement';
import { AssetMeasurement } from '../../../../model/asset-measurement';
import { Kpi } from '../../../../model/kpi';
import { DevicesService } from '../../../../services/devices.service';
import { AssetsService } from '../../../../services/assets.service';
import { KpisService } from '../../../../services/kpis.service';
import { MenuItem } from 'primeng/api';
import { MeasurementType } from '../../../../model/measurement-type';
import { TenantsService } from '../../../../services/tenants.service';
import { SortOrder } from "../../../../model/sort-order";
import { KpiPersistRequestFilters } from "../../../../model/requests/kpi-persist-request-filters";
import { KpiPersistRequestEnabledFilters } from "../../../../model/requests/kpi-persist-request-enabled-filters";

type PageInfo = { offset: number, limit: number };

@Component({
  selector: 'app-kpis-form-filtered-measurements',
  templateUrl: './kpis-form-filtered-measurements.component.html',
  styleUrls: ['./kpis-form-filtered-measurements.component.scss']
})
export class KpisFormFilteredMeasurementsComponent implements OnInit {

  @Input() kpiInfoForm: FormGroup;
  @Input() filters: KpiPersistRequestFilters;
  @Input() enabledFilters: KpiPersistRequestEnabledFilters;
  @Output() filtersChange: EventEmitter<KpiPersistRequestFilters>;
  @Output() enabledFiltersChange: EventEmitter<KpiPersistRequestEnabledFilters>;

  private readonly LOAD_DELAY_MS = 500;

  private loadTimeout: any;

  public deviceMeasurements: DeviceMeasurement[];
  public deviceMeasurementsFirst: number;
  public deviceMeasurementsRows: number;
  public totalDeviceMeasurements: number;
  public loadingDeviceMeasurements: boolean;
  public deviceMeasurementsPreviewVisible: boolean;
  public assetMeasurements: AssetMeasurement[];
  public assetMeasurementsFirst: number;
  public assetMeasurementsRows: number;
  public totalAssetMeasurements: number;
  public loadingAssetMeasurements: boolean;
  public assetMeasurementsPreviewVisible: boolean;
  public kpis: Kpi[];
  public kpisFirst: number;
  public kpisRows: number;
  public totalKpis: number;
  public loadingKpis: boolean;
  public kpisPreviewVisible: boolean;

  public measurementTabs: MenuItem[];
  public measurementTabsActive: MenuItem;

  public measurementTypes: MeasurementType[];

  public infoCollapsed: boolean;
  public demoSensors: any;
  public demoSensorsSelected: any;

  constructor(
    private devicesService: DevicesService,
    private assetsService: AssetsService,
    private kpisService: KpisService,
    private tenantsService: TenantsService
  ) {
    this.filtersChange = new EventEmitter<KpiPersistRequestFilters>();
    this.enabledFiltersChange = new EventEmitter<KpiPersistRequestEnabledFilters>();
    this.totalDeviceMeasurements = 0;
    this.totalAssetMeasurements = 0;
    this.totalKpis = 0;
    this.deviceMeasurements = [];
    this.assetMeasurements = [];
    this.kpis = [];
    this.loadingDeviceMeasurements = false;
    this.loadingAssetMeasurements = false;
    this.loadingKpis = false;
    this.deviceMeasurementsFirst = 0;
    this.assetMeasurementsFirst = 0;
    this.kpisFirst = 0;
    this.deviceMeasurementsRows = 10;
    this.assetMeasurementsRows = 10;
    this.kpisRows = 10;
    this.measurementTabs = [
      {id: 'deviceMeasurements', label: 'model.deviceMeasurements', icon: 'pi pi-fw pi-mobile'},
      {id: 'assetMeasurements', label: 'model.assetMeasurements', icon: 'pi pi-fw pi-map-marker'},
      {id: 'kpis', label: 'model.kpis', icon: 'pi pi-fw pi-chart-line'}
    ];
    this.measurementTabsActive = this.measurementTabs[0];
    this.demoSensors =  [{sensor: 'meterIndex'}, {sensor: 'consumption'}];
    this.demoSensorsSelected =  ['meterIndex', 'consumption'];
    this.infoCollapsed = true;
  }

  ngOnInit() {
    this.loadMeasurementTypes();
    if (this.enabledFilters.deviceMeasurementFilters) {
      this.onApplyDeviceMeasurementFilters();
    }
    if (this.enabledFilters.assetMeasurementFilters) {
      this.onApplyAssetMeasurementFilters();
    }
    if (this.enabledFilters.kpiFilters) {
      this.onApplyKpiFilters();
    }
  }

  public loadMeasurementTypes() {
    const tenant = this.kpiInfoForm.get('tenant').value.uuid;
    this.tenantsService.getTenantMeasurementTypes(tenant).subscribe(response => {
      this.measurementTypes = response.measurementTypes.sort((a, b) => a.sensor > b.sensor ? 1 : -1);
    });
  }

  public onApplyDeviceMeasurementFilters(event?): void {
    this.loadingDeviceMeasurements = true;
    const filters = this.filters.deviceMeasurementFilters;
    this.applyTenantAndOrganization(filters);
    const pageInfo = this.applyEventIfPresent(filters, this.deviceMeasurementsFirst, this.deviceMeasurementsRows, event);
    this.devicesService.getCommonDeviceMeasurements(filters, pageInfo.offset, pageInfo.limit).toPromise().then(response => {
      this.deviceMeasurements = response.deviceMeasurements;
      this.totalDeviceMeasurements = response.totalElements;
      this.loadingDeviceMeasurements = false;
    });
  }

  public onApplyAssetMeasurementFilters(event?): void {
    this.loadingAssetMeasurements = true;
    const filters = this.filters.assetMeasurementFilters;
    this.applyTenantAndOrganization(filters);
    const pageInfo = this.applyEventIfPresent(filters, this.assetMeasurementsFirst, this.assetMeasurementsRows, event);
    this.assetsService.getCommonAssetMeasurements(filters, pageInfo.offset, pageInfo.limit).toPromise().then(response => {
      this.assetMeasurements = response.assetMeasurements;
      this.totalAssetMeasurements = response.totalElements;
      this.loadingAssetMeasurements = false;
    });
  }

  public onApplyKpiFilters(event?): void {
    this.loadingKpis = true;
    const filters = this.filters.kpiFilters;
    this.applyTenantAndOrganization(filters);
    const pageInfo = this.applyEventIfPresent(filters, this.kpisFirst, this.kpisRows, event);
    this.kpisService.getKpis(pageInfo.offset, pageInfo.limit, filters).toPromise().then(response => {
      this.kpis = response.kpis;
      this.totalKpis = response.totalElements;
      this.loadingKpis = false;
    });
  }

  public onClearDeviceMeasurementFilters(): void {
    this.filters.deviceMeasurementFilters = { name: null, tenantUuids: [], organizationUuids: [], measurementTypeSensors: [] };
    this.onApplyDeviceMeasurementFilters();
    this.filtersChange.emit(this.filters);
  }

  public onClearAssetMeasurementFilters(): void {
    this.filters.assetMeasurementFilters = { name: null, tenantUuids: [], organizationUuids: [], measurementTypeSensors: [] };
    this.onApplyAssetMeasurementFilters();
    this.filtersChange.emit(this.filters);
  }

  public onClearKpiFilters(): void {
    this.filters.kpiFilters = { name: null, tenantUuids: [], organizationUuids: [] };
    this.onApplyKpiFilters();
    this.filtersChange.emit(this.filters);
  }

  public switchFilters(filtersControlName: string): void {
    this.enabledFilters[filtersControlName] = !this.enabledFilters[filtersControlName];
    this.enabledFiltersChange.emit(this.enabledFilters);
    if (this.enabledFilters[filtersControlName]) {
      this.applyFiltersOfControl(filtersControlName);
    }
  }

  public loadAfterDelay(modifiedControlName: string) {
    clearTimeout(this.loadTimeout);
    this.loadTimeout = setTimeout(() => {
      this.applyFiltersOfControl(modifiedControlName)
    }, this.LOAD_DELAY_MS);
  }

  private applyFiltersOfControl(filtersControlName: string) {
    this.filtersChange.emit(this.filters);
    if (filtersControlName === 'deviceMeasurementFilters') {
      this.onApplyDeviceMeasurementFilters();
    } else if (filtersControlName === 'assetMeasurementFilters') {
      this.onApplyAssetMeasurementFilters();
    } else if (filtersControlName === 'kpiFilters') {
      this.onApplyKpiFilters();
    }
  }

  private applyTenantAndOrganization(filters: any) {
    const tenantUuid = this.kpiInfoForm.get('tenant').value.uuid;
    const organizationUuid = this.kpiInfoForm.get('organization')?.value?.uuid;
    filters.tenantUuids = [tenantUuid];
    filters.organizationUuids = organizationUuid ? [organizationUuid] : null;
  }

  private applyEventIfPresent(filters: any, first: number, rows: number, event?: any): PageInfo {
    let offset, limit;
    if (event) {
      offset = (event.first / event.rows);
      limit = event.rows;
      filters.sortField = event.sortField;
      filters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    } else {
      offset = first / rows;
      limit = rows;
    }
    return { offset, limit };
  }

}
